import { useState, useEffect, useCallback, useRef } from 'react';
import AuthService, { AuthUser } from '../services/auth';

// Extend Window interface to include our custom properties
declare global {
  interface Window {
    authStateRefreshed?: boolean;
    authStateRefreshTimestamp?: number;
  }
}

interface UseAuthReturn {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (provider?: 'oidc', returnUrl?: string) => void;
  logout: (returnUrl?: string) => void;
  refreshUser: (showLoading?: boolean) => Promise<void>;
  getUserDisplayName: () => string;
  getUserEmail: () => string;
  getUserAvatar: () => string;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  isAgentAdmin: (agentId: string) => boolean;
  isEmployee: () => boolean;
  hasAdminAccess: () => boolean;
  hasFullAdminAccess: () => boolean;
  isTestingAdmin: () => boolean;
  canAccessPolicyTesting: () => boolean;
  canAccessPWMSTesting: () => boolean;
  getUserRoles: () => string[];
  toggleMockAdminMode: () => boolean;
}

export const useAuth = (): UseAuthReturn => {
  // DEPLOYMENT CHECK - This log confirms the latest code is deployed
  console.log('🚀 [useAuth] Hook initialized - Build timestamp:', new Date().toISOString().split('T')[0]);
  
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const hasInitializedRef = useRef(false);

  const authService = AuthService.getInstance();

  // Refresh user information.
  // showLoading=true only on first mount; background re-checks (window focus)
  // must NOT flip isLoading, because that unmounts the entire component tree.
  const refreshUser = useCallback(async (showLoading = false) => {
    if (showLoading) {
      setIsLoading(true);
    }
    try {
      const currentUser = await authService.getCurrentUser();
      console.log('🔐 [useAuth] User loaded:', {
        name: currentUser?.userDetails,
        roles: currentUser?.userRoles,
        hasUser: !!currentUser
      });
      setUser(currentUser);
      const isAuth = currentUser !== null;
      setIsAuthenticated(isAuth);
      
      window.authStateRefreshed = true;
      window.authStateRefreshTimestamp = Date.now();
      
      window.dispatchEvent(new CustomEvent('authStateChanged', { 
        detail: { authenticated: isAuth, timestamp: Date.now() }
      }));
     
    } catch (error) {
      console.error('Error refreshing user:', error);
      setUser(null);
      setIsAuthenticated(false);
      
      window.dispatchEvent(new CustomEvent('authStateChanged', { 
        detail: { authenticated: false, timestamp: Date.now() }
      }));
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, [authService]);

  // Login function (OIDC only)
  const login = useCallback((provider?: 'oidc', returnUrl?: string) => {
   authService.login(provider, returnUrl);
  }, [authService]);

  // Logout function
  const logout = useCallback((returnUrl?: string) => {
    authService.logout(returnUrl);
  }, [authService]);

  // Get user display name
  const getUserDisplayName = useCallback(() => {
    if (!user) return 'Unknown User';
    return authService.getUserDisplayName(user);
  }, [user, authService]);

  // Get user email
  const getUserEmail = useCallback(() => {
    if (!user) return '';
    return authService.getUserEmail(user);
  }, [user, authService]);

  // Get user avatar
  const getUserAvatar = useCallback(() => {
    if (!user) return 'U';
    return authService.getUserAvatar(user);
  }, [user, authService]);

  // Check if user has role
  const hasRole = useCallback((role: string) => {
    if (!user) return false;
    return authService.hasRole(user, role);
  }, [user, authService]);

  // Check if current user is admin
  const isAdmin = useCallback(() => {
    return authService.isAdmin();
  }, [authService]);

  // Check if user has admin access
  const hasAdminAccess = useCallback(() => {
    const result = authService.hasAdminAccess();
    console.log('🎯 [useAuth] hasAdminAccess called, result:', result);
    return result;
  }, [authService]);

  // Check if user has full admin access (excludes testing-only roles)
  const hasFullAdminAccess = useCallback(() => {
    return authService.hasFullAdminAccess();
  }, [authService]);

  // Check if user is testing admin
  const isTestingAdmin = useCallback(() => {
    return authService.isTestingAdmin();
  }, [authService]);

  // Check if user can access policy testing features
  const canAccessPolicyTesting = useCallback(() => {
    return authService.canAccessPolicyTesting();
  }, [authService]);

  // Check if user can access PWMS testing features
  const canAccessPWMSTesting = useCallback(() => {
    return authService.canAccessPWMSTesting();
  }, [authService]);

  // Check if user is super admin
  const isSuperAdmin = useCallback(() => {
    return authService.isSuperAdmin();
  }, [authService]);

  // Check if user is admin for a specific agent (super-admins are admin for all)
  const isAgentAdmin = useCallback((agentId: string) => {
    return authService.isAgentAdmin(agentId);
  }, [authService]);

  // Check if user is employee
  const isEmployee = useCallback(() => {
    return authService.isEmployee();
  }, [authService]);

  // Get all user roles
  const getUserRoles = useCallback(() => {
    return authService.getUserRoles();
  }, [authService]);

  // Check if user has any of the specified roles
  const hasAnyRole = useCallback((roles: string[]) => {
    return authService.hasAnyRole(roles);
  }, [authService]);

  // Toggle mock admin mode (development only)
  const toggleMockAdminMode = useCallback(() => {
    return authService.toggleMockAdminMode();
  }, [authService]);

  // Initialize authentication on mount and check for success signals
  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const checkAuthState = async () => {
      const authSuccess = localStorage.getItem('oidc_auth_success');
      const authTimestamp = localStorage.getItem('oidc_auth_timestamp');
      
      if (authSuccess === 'true' && authTimestamp) {
        const timestamp = parseInt(authTimestamp);
        const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
        
        if (timestamp > fiveMinutesAgo) {
          localStorage.removeItem('oidc_auth_success');
          localStorage.removeItem('oidc_auth_timestamp');
          
          setTimeout(async () => {
            await refreshUser(true);
            
            window.authStateRefreshed = true;
            window.authStateRefreshTimestamp = Date.now();
            window.dispatchEvent(new CustomEvent('authStateChanged', { 
              detail: { authenticated: true, timestamp: Date.now() }
            }));
          }, 100);
          return;
        } else {
          localStorage.removeItem('oidc_auth_success');
          localStorage.removeItem('oidc_auth_timestamp');
        }
      }
      
      // Initial auth check — show loading spinner only on first mount
      await refreshUser(true);
      
      window.authStateRefreshed = true;
      window.authStateRefreshTimestamp = Date.now();
      window.dispatchEvent(new CustomEvent('authStateChanged', { 
        detail: { authenticated: isAuthenticated, timestamp: Date.now() }
      }));
    };
    
    checkAuthState();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for authentication changes (e.g., after login/logout)
  // Background refresh — never shows the loading spinner, debounced to 2s
  useEffect(() => {
    let focusTimeout: ReturnType<typeof setTimeout> | null = null;

    const handleFocus = () => {
      if (focusTimeout) clearTimeout(focusTimeout);
      focusTimeout = setTimeout(() => {
        refreshUser(false);
      }, 2000);
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'oidc_auth_success' && e.newValue === 'true') {
        refreshUser(false);
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorageChange);
      if (focusTimeout) clearTimeout(focusTimeout);
    };
  }, [refreshUser]);

  return {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshUser,
    getUserDisplayName,
    getUserEmail,
    getUserAvatar,
    hasRole,
    hasAnyRole,
    isAdmin,
    isSuperAdmin,
    isAgentAdmin,
    isEmployee,
    hasAdminAccess,
    hasFullAdminAccess,
    isTestingAdmin,
    canAccessPolicyTesting,
    canAccessPWMSTesting,
    getUserRoles,
    toggleMockAdminMode
  };
}; 